'use client';

import { Button } from '@/comps/primitives/button';
import { AiProficiencyReportCard } from '@/domains/pages/ai-proficiency/components/ai-proficiency-report-card';
import { LevelMixChart } from '@/domains/pages/ai-proficiency/components/level-mix-chart';
import { ProficiencyLadder } from '@/domains/pages/ai-proficiency/components/proficiency-ladder';
import { SegmentOverTimeChart, SegmentOverTimeLegend } from '@/domains/pages/ai-proficiency/components/segment-over-time-chart';
import { SegmentPicker } from '@/domains/pages/ai-proficiency/components/segment-picker';
import { SegmentScaleChart, SegmentScaleChartSkeleton } from '@/domains/pages/ai-proficiency/components/segment-scale-chart';
import { SegmentDimensionMappingTrigger } from '@/domains/pages/shared/segment-dimension-mapping-trigger';
import { SegmentPickerRow } from '@/domains/pages/shared/segment-picker-row';
import { MethodologyDrawer } from '@/domains/pages/ai-proficiency/components/methodology-drawer';
import { SegmentScoreCard } from '@/domains/pages/ai-proficiency/components/segment-score-card';
import { useAiProficiencySegmentPageData } from '@/domains/pages/ai-proficiency/hooks/use-ai-proficiency-segment-page-data';
import { useAiProficiencyShellLoading } from '@/domains/pages/ai-proficiency/hooks/use-ai-proficiency-shell-loading';
import { AiProficiencyChartsLoadingSkeleton } from '@/domains/pages/ai-proficiency/ai-proficiency-page-loading';
import { sumLevelUserCounts } from '@/domains/pages/ai-proficiency/utils/ladder';
import { buildNoSegmentsEmptyReason } from '@/domains/pages/ai-proficiency/utils/segment-empty-state';
import { buildSegmentFilterV2Values, pageKeyToSegmentKind, segmentKindLabel } from '@/domains/pages/ai-proficiency/utils/segment-api-payloads';
import { shouldHideNebulyPageBody, useSyncNebulyPageViewState } from '@/domains/pages/hooks/use-sync-nebuly-page-view-state';
import { useNebulyPageTitleSubject } from '@/domains/pages/hooks/use-nebuly-page-title-subject';
import type { NebulyPageBodyProps } from '@/domains/pages/types';
import { TimeRangeKind } from '@/api/generated/model';
import { capitalizeFirstLetter } from '@/utils/capitalize-first-letter';
import { useCreateQueryStringUrl } from '@/hooks/use-create-query-string-url';
import { useProjectsContext } from '@/providers/projects-provider';
import { Info } from 'lucide-react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';
export function AiProficiencySegmentPageBody({
  pageKey,
  timeRange: _timeRange
}: NebulyPageBodyProps) {
  const {
    projectId
  } = useProjectsContext();
  const searchParams = useSearchParams();
  const router = useRouter();
  const createQueryStringUrl = useCreateQueryStringUrl();
  const segmentKind = pageKeyToSegmentKind(pageKey);
  const segmentFromUrl = searchParams.get('segment') ?? undefined;
  const segmentNoun = segmentKind ? segmentKindLabel(segmentKind) : 'segment';
  const segmentNounPlural = segmentKind ? segmentKindLabel(segmentKind, true) : 'segments';
  const data = useAiProficiencySegmentPageData({
    projectId,
    segmentKind: segmentKind!,
    selectedSegmentName: segmentFromUrl
  });
  useNebulyPageTitleSubject(data.selectedSegmentName ? data.formatSegmentName(data.selectedSegmentName) : undefined);
  const [methodologyOpen, setMethodologyOpen] = useState(false);
  useEffect(() => {
    if (!segmentKind || data.segments.length === 0 || segmentFromUrl) return;
    const defaultName = data.segments[0]?.name;
    if (!defaultName) return;
    router.replace(createQueryStringUrl({
      params: {
        segment: defaultName
      }
    }));
  }, [createQueryStringUrl, data.segments, router, segmentFromUrl, segmentKind]);
  const handleSelectSegment = (name: string) => {
    router.replace(createQueryStringUrl({
      params: {
        segment: name
      }
    }));
  };
  const isNoSegmentsEmpty = !data.isInitialLoading && !data.isError && data.segments.length === 0;
  const isScoreEmpty = !data.isInitialLoading && !data.isChartsLoading && !data.isError && data.segments.length > 0 && data.segmentScore.score == null && data.segmentScore.totalUsers === 0;
  const isEmpty = isNoSegmentsEmpty || isScoreEmpty;
  const showPageShellLoading = useAiProficiencyShellLoading(pageKey, data.isInitialLoading, data.isError);
  useSyncNebulyPageViewState({
    isLoading: showPageShellLoading,
    isError: data.isError,
    error: data.error,
    refetch: data.refetch,
    isEmpty,
    emptyReason: isNoSegmentsEmpty && segmentKind ? buildNoSegmentsEmptyReason(segmentKind) : undefined
  });
  const methodologyScopeFilters = useMemo(() => segmentKind && data.selectedSegmentName ? [buildSegmentFilterV2Values(segmentKind, [data.selectedSegmentName])] : [], [data.selectedSegmentName, segmentKind]);
  if (!segmentKind) return null;
  if (showPageShellLoading || data.isError) {
    return null;
  }
  if (shouldHideNebulyPageBody(false, false, isEmpty)) {
    return null;
  }
  const hasLevelBenchmarks = Object.keys(data.levelBenchmarks).length > 0;
  const ladderComparison = hasLevelBenchmarks ? 'bestSegment' : 'none';
  const isChartsPending = data.isInitialLoading || data.isChartsLoading;
  const selectedSegmentDisplayName = data.formatSegmentName(data.selectedSegmentName);
  return <div className="border-b border-b-lightborder" data-testid={`nebuly-page-body-${pageKey}`}>
      <SegmentPickerRow trailing={<SegmentDimensionMappingTrigger segmentKind={segmentKind} />}>
        <SegmentPicker segments={data.segments} selectedName={data.selectedSegmentName} onSelect={handleSelectSegment} />
      </SegmentPickerRow>

      {isChartsPending || data.segments.length > 0 ? <AiProficiencyReportCard title={`${capitalizeFirstLetter(segmentNoun)} proficiency scores`} className="border-b border-b-lightborder">
          {isChartsPending ? <SegmentScaleChartSkeleton /> : <SegmentScaleChart segments={data.segments} median={data.segmentScoreStats.median} orgScore={data.orgScore.score} selectedName={data.selectedSegmentName} onSelect={handleSelectSegment} />}
        </AiProficiencyReportCard> : null}

      {isChartsPending ? <AiProficiencyChartsLoadingSkeleton showSegmentLadderBar /> : <>
          <div className="grid grid-cols-3 items-stretch border-b border-b-lightborder">
            <div className="col-span-1 flex border-r border-r-lightborder">
              <AiProficiencyReportCard title="AI Proficiency score" className="min-h-0 flex-1" contentClassName="flex min-h-0 flex-1 flex-col justify-center !py-3" headerTrailing={<Button variant="outline" size="sm" className="h-7 gap-1.5 rounded-full px-3 text-xs text-muted-foreground hover:text-foreground" onClick={() => setMethodologyOpen(true)}>
                    <Info className="size-3.5" />
                    How it&apos;s calculated
                  </Button>}>
                <SegmentScoreCard segmentName={data.selectedSegmentName} segmentDisplayName={selectedSegmentDisplayName} segmentScore={data.segmentScore} segmentRank={data.segmentRank} segmentCount={data.segments.length} segmentNoun={segmentNoun} segmentNounPlural={segmentNounPlural} scoreStats={data.segmentScoreStats} peers={data.segments} timeRangeKind={TimeRangeKind['30_days']} />
              </AiProficiencyReportCard>
            </div>

            <div className="col-span-2 flex">
              <AiProficiencyReportCard title="AI Proficiency over time" className="min-h-0 flex-1" contentClassName="flex min-h-0 flex-1 flex-col">
                <div className="flex min-h-0 flex-1 flex-col">
                  <SegmentOverTimeLegend selectedDisplayName={selectedSegmentDisplayName} segmentNoun={segmentNoun} segmentNounPlural={segmentNounPlural} className="mb-4 shrink-0" />
                  <div className="min-h-55 flex-1">
                    <SegmentOverTimeChart labels={data.trendLabels} selectedDisplayName={selectedSegmentDisplayName} selectedScores={data.segmentScoresOverTime} orgScores={data.orgScoresOverTime} otherSegmentScores={data.otherSegmentScoresOverTime} worstBestEnvelope={data.worstBestEnvelope} segmentNoun={segmentNoun} segmentNounPlural={segmentNounPlural} computeTrendRank={data.computeTrendRank} segmentCount={data.segments.length} />
                  </div>
                </div>
              </AiProficiencyReportCard>
            </div>
          </div>

          <div className="grid grid-cols-4 items-start border-b border-b-lightborder">
            <div className="col-span-4">
              <AiProficiencyReportCard title="AI Proficiency groups" contentClassName="p-0">
                <ProficiencyLadder levels={data.levels} orgLevels={data.orgLevels} totalUsers={sumLevelUserCounts(data.levels)} levelBenchmarks={data.levelBenchmarks} comparison={ladderComparison} segmentNoun={segmentNoun} segmentNounPlural={segmentNounPlural} subjectLabel={selectedSegmentDisplayName} />
              </AiProficiencyReportCard>
            </div>
          </div>

          <div className="grid grid-cols-4 items-start">
            <div className="col-span-4">
              <AiProficiencyReportCard title="AI Proficiency groups progress over time" contentClassName="p-0">
                <LevelMixChart labels={data.trendLabels} levelMix={data.levelMixOverTime} levelCounts={data.levelCountsOverTime} migration={data.migration} timeRangeLabel={data.migrationTimeRangeLabel} />
              </AiProficiencyReportCard>
            </div>
          </div>
        </>}

      <MethodologyDrawer open={methodologyOpen} onOpenChange={setMethodologyOpen} projectId={projectId} scopeFilters={methodologyScopeFilters} queryKeySuffix={segmentKind} />
    </div>;
}